import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resendVerification } from '../lib/auth-api';
import { fetchOnboarding } from '../lib/onboarding-api';
import { useAgentStore } from '../stores/agent';
import styles from './Login.module.css';
import own from './Signup.module.css';
import { AnimatedContent } from '../components/react-bits';

// Two states on one route:
//   #/verify-email?email=…   just signed up (or tried to sign in unverified):
//                            "check your inbox", with a resend
//   #/verify-email?token=…   the emailed link: spend it, sign in, resume setup
//
// The resend answer is the same whether or not the address is waiting, so
// the confirmation says "if", and never promises delivery.

/** Matches the server's per-agent cooldown (VERIFY_RESEND_COOLDOWN_MS). */
const RESEND_COOLDOWN_S = 60;

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  // Keyed by token: opening a newer link in a tab still showing an older one
  // must spend the new link, not keep the old attempt's result.
  return token ? <SpendLink key={token} token={token} /> : <CheckInbox initialEmail={params.get('email') ?? ''} />;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className={styles.root}>
      <div className={styles.studioGlow} aria-hidden />
      <AnimatedContent trigger="mount" distance={20} duration={0.6} ease="power3.out" className={`${styles.modalCard} ${own.narrowCard}`}>
        <div className={styles.formCol}>
          <div className={styles.topBrandRow}>
            <Link to="/" className={styles.brandPill} title="Openline Home">
              <span className={styles.brandPillDot} />
              <span>Openline</span>
            </Link>
          </div>
          <div className={styles.headerBlock}>
            <h1 className={styles.title}>{title}</h1>
          </div>
          <div className={own.stack}>{children}</div>
        </div>
      </AnimatedContent>
    </main>
  );
}

function SpendLink({ token }: { token: string }) {
  const navigate = useNavigate();
  const verifyEmail = useAgentStore((s) => s.verifyEmail);
  const [failure, setFailure] = useState<string | null>(null);
  // A link is single-use: StrictMode's double effect (dev) must not spend it
  // twice and then report the second, failed attempt.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const result = await verifyEmail(token);
      if (!result.ok) {
        setFailure(result.reason);
        return;
      }
      // Resume where setup left off; a pre-existing account has no wizard.
      const onboarding = await fetchOnboarding().catch(() => null);
      navigate(onboarding?.nextStep ? '/onboarding' : '/dashboard', { replace: true });
    })();
  }, [token, verifyEmail, navigate]);

  if (!failure) {
    return (
      <Card title="Confirming your email…">
        <p className={own.lede} role="status">
          One moment.
        </p>
      </Card>
    );
  }
  if (failure === 'invalid-token') {
    return (
      <Card title="This link can't be used">
        <p className={own.lede}>
          It has expired, was already used, or a newer link has been sent since. Links work once and expire after 24
          hours.
        </p>
        <p className={own.lede}>
          If you already confirmed, just <Link to="/login">sign in</Link>. Otherwise, ask for a new link below.
        </p>
        <ResendForm initialEmail="" />
      </Card>
    );
  }
  return (
    <Card title="We couldn't confirm your email">
      <p className={styles.error} role="alert">
        {failure === 'account-disabled'
          ? 'This account has been disabled. Contact support if you think this is a mistake.'
          : failure === 'rate-limited'
            ? 'Too many attempts from this network. Wait a few minutes and open the link again.'
            : 'Could not reach the server. Check your connection and open the link again.'}
      </p>
      <p className={own.lede}>
        <Link to="/login">← Back to sign in</Link>
      </p>
    </Card>
  );
}

function CheckInbox({ initialEmail }: { initialEmail: string }) {
  return (
    <Card title="Check your inbox">
      <p className={own.lede}>
        {initialEmail ? (
          <>
            We sent a confirmation link to <strong>{initialEmail}</strong>.
          </>
        ) : (
          'We sent you a confirmation link.'
        )}{' '}
        Open it to finish creating your account — it works once and expires in 24 hours.
      </p>
      <p className={own.lede}>Nothing there? Check your spam folder, or send another link.</p>
      <ResendForm initialEmail={initialEmail} />
      <p className={own.lede}>
        <Link to="/login">← Back to sign in</Link>
      </p>
    </Card>
  );
}

function ResendForm({ initialEmail }: { initialEmail: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (sending || cooldown > 0 || !email.trim()) return;
    setSending(true);
    setStatus('idle');
    try {
      await resendVerification(email.trim());
      setStatus('sent');
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      setStatus('error');
    } finally {
      setSending(false);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {!initialEmail && (
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} htmlFor="resend-email">
            Email
          </label>
          <div className={styles.inputWrapper}>
            <input
              id="resend-email"
              className={styles.pillInput}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sending}
            />
          </div>
        </div>
      )}
      {status === 'sent' && (
        <p className={styles.notice} role="status">
          If that address is waiting to be confirmed, a new link is on its way. Older links no longer work.
        </p>
      )}
      {status === 'error' && (
        <p className={styles.error} role="alert">
          Could not reach the server. Try again in a moment.
        </p>
      )}
      <button type="submit" className={styles.submitBtn} disabled={sending || cooldown > 0 || !email.trim()}>
        {sending ? 'Sending…' : cooldown > 0 ? `Send another link in ${cooldown}s` : 'Send a new link'}
      </button>
    </form>
  );
}
